import styles from './SharedWithOtherCourses.module.less'
import React, { ReactElement, useCallback } from 'react'
import { Modal, Tag, Tooltip } from 'antd'
import { ForkOutlined } from '@ant-design/icons'
import { useRecoilValue } from 'recoil'
import { SharedNodeOccurrence } from 'common/src/outlines/types'
import { hashContentPath } from 'common/src/ContentPath'
import { hasUnsavedChangesSelector } from '@/atoms/unsavedChanges'
import { setFrontendActiveCourseForContentCreator } from '@/api/cb/contentCreatorAccount'
import { syncErrorMessage } from '@/utils/syncMessages'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { SharedWithOtherCoursesProps } from './SharedWithOtherCourses.types'

/**
 * Flags a node whose id also exists in other courses (copied chapters keep
 * their id) and links to each copy. Edits do not propagate between copies, so
 * this is how a content creator finds the siblings to keep in sync.
 */
export const SharedWithOtherCourses: React.FC<SharedWithOtherCoursesProps> = (
    props
): ReactElement => {
    const hasUnsavedChanges = useRecoilValue(hasUnsavedChangesSelector)

    const openInOtherCourse = useCallback(
        async (occurrence: SharedNodeOccurrence): Promise<void> => {
            const switchCourse = async (): Promise<void> => {
                const response = await setFrontendActiveCourseForContentCreator(
                    occurrence.courseName
                )
                if (response.data.isError) {
                    syncErrorMessage(
                        `Unable to switch to course: ${occurrence.courseName}. Please refresh and try again.`
                    )
                    return
                }
                // hard refresh resets recoil, same as ChooseFrontendDisplayedCourse;
                // the contentPath query opens the tree at the copied node
                window.location.href = `${window.location.origin}${
                    UiLayoutConstant.PRIMARY_DASHBOARD_PAGE
                }?contentPath=${encodeURIComponent(
                    hashContentPath(occurrence.contentPath)
                )}`
            }
            if (hasUnsavedChanges) {
                Modal.confirm({
                    title: 'You have unsaved changes',
                    content: `Switching to ${occurrence.coursePrettyName} will discard them. Continue?`,
                    okText: 'Switch course',
                    cancelText: 'Stay here',
                    onOk: switchCourse,
                })
                return
            }
            await switchCourse()
        },
        [hasUnsavedChanges]
    )

    const numOtherCourses = props.sharedWith.length
    return (
        <div
            className={styles.sharedWithOtherCourses}
            onClick={(event) => event.stopPropagation()}
        >
            <Tooltip
                title={
                    'A node with this id also exists in the courses listed. Edits here do not carry over; open each copy to keep them in sync.'
                }
            >
                <Tag icon={<ForkOutlined />} color={'gold'}>
                    {`Also in ${numOtherCourses} other course${
                        numOtherCourses === 1 ? '' : 's'
                    }`}
                </Tag>
            </Tooltip>
            {props.sharedWith.map(
                (occurrence): ReactElement => (
                    <Tooltip
                        key={`${occurrence.courseName}|${hashContentPath(
                            occurrence.contentPath
                        )}`}
                        title={`Open in ${occurrence.coursePrettyName}`}
                    >
                        <a
                            className={styles.otherCourseLink}
                            onClick={() => openInOtherCourse(occurrence)}
                        >
                            {occurrence.courseAbbreviation ||
                                occurrence.courseName}
                        </a>
                    </Tooltip>
                )
            )}
        </div>
    )
}
